<template>
  <div class="day" :class="{ details: !!details, extended: extended }">
    <div class="schedule-nutrients">
      <DietDetailDaySchedule />
      <DietDetailDayNutrients />
    </div>
    <div
      ref="searchDetailsRef"
      class="search-details"
      :style="`height: calc(100vh - ${height}px);`"
    >
      <DietDetailDaySearch v-show="!details" />
      <DietDetailDayDishOrIngredient v-if="details" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useEventListener } from '@vueuse/core'
  import { storeToRefs } from 'pinia'
  import { computed, onMounted, onUpdated, ref, watch } from 'vue'
  import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'

  import DietDetailDayDishOrIngredient from '@/components/diets/DietDetailDayDishOrIngredient.vue'
  import DietDetailDayNutrients from '@/components/diets/DietDetailDayNutrients.vue'
  import DietDetailDaySchedule from '@/components/diets/DietDetailDaySchedule.vue'
  import DietDetailDaySearch from '@/components/diets/DietDetailDaySearch.vue'
  import { useDietsStore } from '@/store/dietsStore'
  import { getDietDaysCount } from '@/utils/dietDays'
  import { reportError } from '@/utils/reportError'

  const height = ref(1)
  const dietStore = useDietsStore()
  const { extended, details, diet } = storeToRefs(dietStore)
  const route = useRoute()
  const router = useRouter()
  const dayIndex = computed(() => {
    const index = parseInt(route.params.dayIndex as string)
    if (isNaN(index)) {
      reportError(new Error('Invalid day index'), '', { dayIndex: route.params.dayIndex })
      return 0
    }
    return index
  })

  watch(
    [dayIndex, diet],
    ([index]) => {
      const daysCount = getDietDaysCount(diet.value)
      if (index >= daysCount) {
        void router.replace({
          name: 'DietDetailDay',
          params: {
            ...route.params,
            dayIndex: String(Math.max(0, daysCount - 1)),
          },
        })
      }
    },
    { immediate: true },
  )
  const searchDetailsRef = ref<HTMLElement>()

  const calculateHeight = () => {
    if (searchDetailsRef.value) {
      const heightDifference = Math.round(searchDetailsRef.value.getBoundingClientRect().top + 20)
      if (heightDifference !== height.value) {
        height.value = heightDifference
      }
    }
  }

  onMounted(() => {
    dietStore.clearUndoRedo()
    const storageMealIndex = sessionStorage.getItem('redirectMealIndex')
    const storageDishOrIngredientIndex = sessionStorage.getItem('redirectDishOrIngredientIndex')
    sessionStorage.removeItem('redirectMealIndex')
    sessionStorage.removeItem('redirectDishOrIngredientIndex')

    if (storageMealIndex && storageDishOrIngredientIndex) {
      const mealIndex = parseInt(storageMealIndex)
      const dishOrIngredientIndex = parseInt(storageDishOrIngredientIndex)
      if (isNaN(mealIndex) || isNaN(dishOrIngredientIndex)) {
        reportError(new Error('Invalid meal or dish index'), '', {
          dishOrIngredientIndex,
          mealIndex,
        })
        return
      }
      dietStore.updateDetails({
        dayIndex: dayIndex.value,
        dishOrIngredientIndex,
        mealIndex,
      })
    } else {
      dietStore.updateDetails()
    }
    setTimeout(calculateHeight, 0)
  })

  onUpdated(calculateHeight)

  onBeforeRouteUpdate((_, __, next) => {
    dietStore.updateDetails()
    next()
  })

  useEventListener(document, 'scroll', calculateHeight)
  useEventListener(() => document.querySelector('.app-content'), 'scroll', calculateHeight)
</script>

<style scoped>
  .day {
    display: flex;
    flex-direction: row;
  }

  .schedule-nutrients {
    flex-grow: 1;
    width: 60%;
    min-width: 490px;
    margin-right: 13px;
    margin-bottom: 20px;
    background: #fff;
    box-shadow: 2px 4px 4px 0 rgb(232 232 232 / 50%);
  }

  .search-details {
    position: sticky;
    top: 20px;
    width: 40%;
    min-width: 505px;
  }

  .extended .schedule-nutrients {
    display: none;
  }

  .extended .search-details {
    width: 100%;
  }
</style>
